/**
 * Utility function to seed student history filter options into Firestore
 * This can be run from the browser console while authenticated as an admin
 *
 * Usage:
 * 1. Open browser console while logged in as admin
 * 2. Import and call: seedStudentHistoryFilters()
 */

import {
  collection,
  getDocs,
  query,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase.js';
import { LEGACY_STUDENT_HISTORY_OPTIONS } from '../services/disciplineConfigService.js';

/**
 * Check if student history filters already exist in Firestore
 */
export async function studentHistoryFiltersExist() {
  try {
    const q = query(collection(db, 'studentHistoryFilters'));
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking student history filters:', error);
    throw error;
  }
}

/**
 * Seed student history filters into Firestore
 * @param {boolean} force - Force seeding even if filters already exist
 * @returns {Promise<Object>} Result object with success status and count
 */
export async function seedStudentHistoryFilters(force = false) {
  console.log('🚀 Starting student history filters seeding...\n');

  try {
    // Check if data already exists
    const exists = await studentHistoryFiltersExist();
    if (exists && !force) {
      const message = '⚠️  Student history filters already exist in Firestore. Use seedStudentHistoryFilters(true) to force re-seed.';
      console.warn(message);
      return {
        success: false,
        message,
        count: 0
      };
    }

    // Convert filter options to array
    const filtersToSeed = Object.values(LEGACY_STUDENT_HISTORY_OPTIONS);

    console.log(`📝 Preparing to seed ${filtersToSeed.length} student history filters:\n`);
    filtersToSeed.forEach(filter => {
      console.log(`   - ${filter.label} (${filter.value || 'null'})`);
    });
    console.log('');

    // Seed the data using batch write
    const batch = writeBatch(db);
    const collectionRef = collection(db, 'studentHistoryFilters');

    filtersToSeed.forEach((filterData, index) => {
      const docRef = doc(collectionRef);
      batch.set(docRef, {
        ...filterData,
        sortOrder: index,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    });

    await batch.commit();

    console.log('✅ Seeding completed successfully!\n');
    console.log('Summary:');
    console.log(`   - ${filtersToSeed.length} student history filters added to Firestore`);
    console.log('   - Collection: studentHistoryFilters');
    console.log('   - All filters set to active: true\n');

    // Verify the seeding
    console.log('🔍 Verifying seeding...');
    const q = query(collectionRef);
    const snapshot = await getDocs(q);
    console.log(`✓ Found ${snapshot.size} student history filters in Firestore\n`);

    console.log('🎉 Seeding complete! You can now:');
    console.log('   1. Use the StudentHistoryFiltersManager UI to manage filters');
    console.log('   2. Add new custom history filters');
    console.log('   3. Edit or deactivate existing filters\n');

    return {
      success: true,
      message: 'Student history filters seeded successfully',
      count: filtersToSeed.length
    };
  } catch (error) {
    console.error('❌ Seeding failed:', error);
    console.error('\nError details:', error.message);
    return {
      success: false,
      message: `Seeding failed: ${error.message}`,
      count: 0,
      error
    };
  }
}

/**
 * Get current student history filters from Firestore
 */
export async function getStudentHistoryFiltersFromFirestore() {
  try {
    const q = query(collection(db, 'studentHistoryFilters'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching student history filters:', error);
    throw error;
  }
}

// Make available in window for console access
if (typeof window !== 'undefined') {
  window.seedStudentHistoryFilters = seedStudentHistoryFilters;
  window.getStudentHistoryFiltersFromFirestore = getStudentHistoryFiltersFromFirestore;
  window.studentHistoryFiltersExist = studentHistoryFiltersExist;
}

export default seedStudentHistoryFilters;
